import React, { useState, useEffect } from 'react';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return isMobile;
};

const Legal = () => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('privacy');

  const s = {
    page: { minHeight: '100vh', background: '#0a0a0a', paddingTop: isMobile ? '88px' : '100px', paddingBottom: '80px', fontFamily: 'Jost, sans-serif' },
    container: { maxWidth: '760px', margin: '0 auto', padding: isMobile ? '0 20px' : '0 32px' },
    eyebrow: { fontSize: '0.62rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '10px' },
    pageTitle: { fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '2rem' : '3rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '8px', lineHeight: 1 },
    updated: { fontSize: '0.65rem', letterSpacing: '0.1em', color: '#3a3a3a', marginBottom: '40px' },
    tabs: { display: 'flex', borderBottom: '1px solid #1e1e1e', marginBottom: '48px' },
    tab: { padding: isMobile ? '12px 20px' : '14px 32px', fontSize: '0.62rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#6b6460', cursor: 'pointer', border: 'none', background: 'none', fontFamily: 'Jost, sans-serif', borderBottom: '2px solid transparent', marginBottom: '-1px', transition: 'all 0.2s ease' },
    tabActive: { padding: isMobile ? '12px 20px' : '14px 32px', fontSize: '0.62rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#c9a96e', cursor: 'pointer', border: 'none', background: 'none', fontFamily: 'Jost, sans-serif', borderBottom: '2px solid #c9a96e', marginBottom: '-1px', transition: 'all 0.2s ease' },
    section: { marginBottom: '48px' },
    sectionTitle: { fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #1a1a1a' },
    body: { fontSize: '0.85rem', color: '#6b6460', lineHeight: 1.9, fontWeight: 300, marginBottom: '14px' },
    highlight: { background: '#111111', border: '1px solid #1e1e1e', padding: '20px 24px', marginBottom: '14px' },
    highlightText: { fontSize: '0.82rem', color: '#a09890', lineHeight: 1.8, fontWeight: 300 },
    list: { paddingLeft: '0', margin: '0 0 14px', listStyle: 'none' },
    listItem: { fontSize: '0.82rem', color: '#6b6460', lineHeight: 1.9, fontWeight: 300, paddingLeft: '16px', position: 'relative', marginBottom: '4px' },
    bullet: { position: 'absolute', left: 0, color: '#c9a96e' },
    email: { color: '#c9a96e', textDecoration: 'none' },
    divider: { borderTop: '1px solid #1a1a1a', margin: '0 0 48px' },
  };

  const Li = ({ children }) => (
    <li style={s.listItem}>
      <span style={s.bullet}>—</span>
      {children}
    </li>
  );

  return (
    <div style={s.page}>
      <div style={s.container}>
        <p style={s.eyebrow}>Legal</p>
        <h1 style={s.pageTitle}>Legal Information</h1>
        <p style={s.updated}>Last updated: June 2026</p>

        <div style={s.tabs}>
          <button style={activeTab === 'privacy' ? s.tabActive : s.tab} onClick={() => setActiveTab('privacy')}>Privacy Policy</button>
          <button style={activeTab === 'retention' ? s.tabActive : s.tab} onClick={() => setActiveTab('retention')}>Data Retention</button>
          <button style={activeTab === 'terms' ? s.tabActive : s.tab} onClick={() => setActiveTab('terms')}>Terms of Service</button>
          <button style={activeTab === 'support' ? s.tabActive : s.tab} onClick={() => setActiveTab('support')}>Support</button>
        </div>

        {/* ── Privacy Policy ── */}
        {activeTab === 'privacy' && (
          <div>
            <div style={s.highlight}>
              <p style={{ ...s.highlightText, color: '#c9a96e', marginBottom: '6px', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase' }}>Summary</p>
              <p style={s.highlightText}>Flakon® collects only what's needed to run the service. We don't sell your data, show you ads, or share your information with third parties except where necessary to operate. You can delete your account and all your data at any time.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>1. Who We Are</h2>
              <p style={s.body}>Flakon® ("we", "us", "our") is a personal fragrance companion app available at flakon.app. We are the data controller for information collected through the app. If you have questions about this policy, contact us at <a href="mailto:support@flakon.app" style={s.email}>support@flakon.app</a>.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>2. What We Collect</h2>
              <p style={s.body}>When you create an account and use Flakon, we collect the following information:</p>
              <div style={s.highlight}>
                <p style={{ ...s.highlightText, marginBottom: '10px', fontWeight: 400, color: '#f5f0eb' }}>Account information</p>
                <ul style={s.list}>
                  <Li>Username and email address</Li>
                  <Li>Password (stored as a secure hash — we never store your actual password)</Li>
                  <Li>Profile picture (if uploaded)</Li>
                  <Li>Bio and favourite fragrance house (if provided)</Li>
                </ul>
              </div>
              <div style={s.highlight}>
                <p style={{ ...s.highlightText, marginBottom: '10px', fontWeight: 400, color: '#f5f0eb' }}>Fragrance & usage data</p>
                <ul style={s.list}>
                  <Li>Fragrances added to your library and bottle levels</Li>
                  <Li>Journal entries including wear dates, occasions, moods and notes</Li>
                  <Li>Fragrance preferences set during onboarding or in your profile</Li>
                </ul>
              </div>
              <div style={s.highlight}>
                <p style={{ ...s.highlightText, marginBottom: '10px', fontWeight: 400, color: '#f5f0eb' }}>Location data</p>
                <ul style={s.list}>
                  <Li>Your approximate location (latitude and longitude), requested via your browser only when you use the home screen</Li>
                  <Li>We use this solely to fetch local weather for scent recommendations</Li>
                  <Li>Location is never stored — it is used in real time and discarded</Li>
                </ul>
              </div>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>3. How We Use Your Data</h2>
              <ul style={s.list}>
                <Li>To operate your account and provide the Flakon service</Li>
                <Li>To generate personalised fragrance recommendations based on your preferences and local weather</Li>
                <Li>To send transactional emails (account verification, password resets)</Li>
                <Li>To show your public profile and wears to other users (only if your account is set to public)</Li>
                <Li>To improve the app based on aggregated, anonymous usage patterns</Li>
              </ul>
              <p style={s.body}>We do not use your data for advertising. We do not sell or rent your data to any third party.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>4. Third-Party Services</h2>
              <p style={s.body}>Flakon uses the following third-party services to operate:</p>
              <ul style={s.list}>
                <Li><strong style={{ color: '#a09890' }}>Railway</strong> — our backend and database hosting provider (EU/US infrastructure)</Li>
                <Li><strong style={{ color: '#a09890' }}>Resend</strong> — for sending transactional emails</Li>
                <Li><strong style={{ color: '#a09890' }}>Cloudinary</strong> — for storing profile pictures</Li>
                <Li><strong style={{ color: '#a09890' }}>OpenWeatherMap</strong> — to fetch weather data based on your location</Li>
              </ul>
              <p style={s.body}>Each of these services has their own privacy policy. We share only the minimum data required for each service to function.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>5. Your Rights (GDPR)</h2>
              <p style={s.body}>If you are in the UK or EU, you have the following rights under GDPR:</p>
              <ul style={s.list}>
                <Li><strong style={{ color: '#a09890' }}>Right of access</strong> — request a copy of the data we hold about you</Li>
                <Li><strong style={{ color: '#a09890' }}>Right to rectification</strong> — correct inaccurate data via your profile settings</Li>
                <Li><strong style={{ color: '#a09890' }}>Right to erasure</strong> — delete your account and all associated data at any time via Profile → Security → Delete Account</Li>
                <Li><strong style={{ color: '#a09890' }}>Right to portability</strong> — request an export of your data</Li>
                <Li><strong style={{ color: '#a09890' }}>Right to object</strong> — contact us to object to any processing</Li>
              </ul>
              <p style={s.body}>To exercise any of these rights, contact us at <a href="mailto:support@flakon.app" style={s.email}>support@flakon.app</a>.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>6. Cookies</h2>
              <p style={s.body}>Flakon does not use tracking cookies or advertising cookies. We use browser local storage solely to keep you logged in between sessions (storing your authentication token). No third-party cookies are set.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>7. Children</h2>
              <p style={s.body}>Flakon is not directed at children under 13. We do not knowingly collect personal data from anyone under 13. If you believe a child has created an account, please contact us and we will delete it.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>8. Changes to This Policy</h2>
              <p style={s.body}>We may update this policy as Flakon grows. We will notify you of significant changes via email or an in-app notice. Continued use of the app after changes constitutes acceptance of the updated policy.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>9. Contact</h2>
              <p style={s.body}>For any privacy-related questions or requests, contact us at <a href="mailto:support@flakon.app" style={s.email}>support@flakon.app</a>.</p>
            </div>
          </div>
        )}

        {/* ── Terms of Service ── */}
        {activeTab === 'terms' && (
          <div>
            <div style={s.highlight}>
              <p style={{ ...s.highlightText, color: '#c9a96e', marginBottom: '6px', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase' }}>Summary</p>
              <p style={s.highlightText}>By using Flakon you agree to these terms. We provide the service in good faith and ask that you use it responsibly. We reserve the right to suspend accounts that misuse the platform.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>1. Acceptance of Terms</h2>
              <p style={s.body}>By creating an account or using Flakon® ("the Service"), you agree to be bound by these Terms of Service. If you do not agree, please do not use the Service. These terms apply to all users of flakon.app.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>2. Who Can Use Flakon</h2>
              <p style={s.body}>You must be at least 13 years old to use Flakon. By using the Service, you confirm that you meet this requirement. If you are under 18, you should have parental consent.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>3. Your Account</h2>
              <ul style={s.list}>
                <Li>You are responsible for maintaining the security of your account and password</Li>
                <Li>You must provide accurate information when creating your account</Li>
                <Li>You may not share your account with others or create accounts on behalf of others</Li>
                <Li>You must notify us immediately at <a href="mailto:support@flakon.app" style={s.email}>support@flakon.app</a> if you suspect unauthorised access to your account</Li>
              </ul>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>4. Acceptable Use</h2>
              <p style={s.body}>You agree not to use Flakon to:</p>
              <ul style={s.list}>
                <Li>Post content that is abusive, hateful, or harmful to others</Li>
                <Li>Impersonate other users or persons</Li>
                <Li>Attempt to access other users' accounts or data</Li>
                <Li>Reverse engineer, scrape, or exploit the Service or its data</Li>
                <Li>Use the Service for any unlawful purpose</Li>
              </ul>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>5. Your Content</h2>
              <p style={s.body}>You retain ownership of the content you create on Flakon — your journal entries, library, profile, and preferences. By using the Service, you grant us a limited licence to store and display your content as necessary to operate the Service. We do not claim ownership of your data.</p>
              <p style={s.body}>You can delete all your content at any time by deleting your account.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>6. The Service</h2>
              <p style={s.body}>Flakon is provided on an "as is" basis. We aim to keep the Service available at all times but cannot guarantee uninterrupted access. We reserve the right to modify, suspend, or discontinue any part of the Service at any time with reasonable notice where possible.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>7. Suspension & Termination</h2>
              <p style={s.body}>We reserve the right to suspend or terminate accounts that violate these terms, engage in abuse, or otherwise misuse the platform. You may delete your own account at any time via Profile → Security → Delete Account.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>8. Limitation of Liability</h2>
              <p style={s.body}>To the maximum extent permitted by law, Flakon and its owner shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Service. Our total liability to you shall not exceed the amount you have paid us in the past 12 months (if any).</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>9. Intellectual Property</h2>
              <p style={s.body}>Flakon® is a registered trademark of Anthony Mulligan (UK Trade Mark No. UK00004354433), registered in Classes 3 and 42. The Flakon name, logo, and all associated branding are protected. You may not use the Flakon name or branding without prior written permission.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>10. Governing Law</h2>
              <p style={s.body}>These terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>11. Changes to These Terms</h2>
              <p style={s.body}>We may update these terms as Flakon grows. We will notify you of significant changes via email or an in-app notice. Continued use of the Service after changes constitutes acceptance of the updated terms.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>12. Contact</h2>
              <p style={s.body}>Questions about these terms? Contact us at <a href="mailto:support@flakon.app" style={s.email}>support@flakon.app</a>.</p>
            </div>
          </div>
        )}

        {/* ── Support ── */}
        {activeTab === 'support' && (
          <div>
            <div style={s.highlight}>
              <p style={{ ...s.highlightText, color: '#c9a96e', marginBottom: '6px', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase' }}>Get in touch</p>
              <p style={s.highlightText}>We genuinely read every message. Whether you've found a bug, have a suggestion, or just want to say hello — reach out.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>Email Support</h2>
              <p style={s.body}>The best way to reach us is by email. We aim to respond within 2 business days.</p>
              <div style={{ ...s.highlight, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <p style={{ ...s.highlightText, fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', color: '#c9a96e', margin: 0 }}>support@flakon.app</p>
                <a href="mailto:support@flakon.app" style={{ background: '#c9a96e', border: 'none', padding: '12px 28px', color: '#0a0a0a', fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer', textDecoration: 'none', display: 'inline-block' }}>
                  Send Email
                </a>
              </div>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>Common Questions</h2>
              {[
                { q: 'How do I delete my account?', a: 'Go to Profile → Security → Delete Account. This permanently removes all your data immediately.' },
                { q: 'I did not receive a verification email', a: 'Check your spam folder first. If it is not there, go back to the sign-in page and use the Resend verification option. If you still have issues, email us.' },
                { q: 'A fragrance is missing or has incorrect information', a: "You can submit a fragrance via the Discover page. If an existing fragrance has wrong notes or details, email us and we'll fix it." },
                { q: 'I found a bug', a: 'Please email us with as much detail as possible — what you were doing, what happened, and what device/browser you were using. Screenshots are very helpful.' },
                { q: 'How do I make my profile public?', a: 'Go to Profile → your profile is private by default. You can enable a public profile in your account settings.' },
              ].map(({ q, a }, i) => (
                <div key={i} style={{ ...s.highlight, marginBottom: '12px' }}>
                  <p style={{ ...s.highlightText, color: '#f5f0eb', marginBottom: '8px', fontWeight: 400 }}>{q}</p>
                  <p style={{ ...s.highlightText, margin: 0 }}>{a}</p>
                </div>
              ))}
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>Data & Privacy Requests</h2>
              <p style={s.body}>To request a copy of your data or to exercise any of your GDPR rights, email us at <a href="mailto:support@flakon.app" style={s.email}>support@flakon.app</a> with the subject line "Data Request". We will respond within 30 days as required by law.</p>
            </div>
          </div>
        )}

        {/* ── Data Retention ── */}
        {activeTab === 'retention' && (
          <div>
            <div style={s.highlight}>
              <p style={{ ...s.highlightText, color: '#c9a96e', marginBottom: '6px', fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase' }}>Summary</p>
              <p style={s.highlightText}>We keep your data for as long as your account is active. When you delete your account, all your personal data is permanently removed from our systems immediately. Location data is never stored at all.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>1. Account Data</h2>
              <p style={s.body}>Your account data — including your username, email, password hash, profile picture, bio, and preferences — is retained for the lifetime of your account. When you delete your account, this data is permanently and immediately deleted from our database.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>2. Fragrance Library & Journal</h2>
              <p style={s.body}>All fragrances in your library and all journal entries are tied to your account. These are retained for as long as your account exists and are permanently deleted when you close your account. There is no partial deletion — deleting your account removes everything.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>3. Location Data</h2>
              <div style={s.highlight}>
                <p style={s.highlightText}>Location data is <strong style={{ color: '#f5f0eb' }}>never stored</strong>. When you open the Flakon home screen, your browser requests your GPS coordinates. These are sent directly to OpenWeatherMap to fetch local weather conditions and are then discarded. We do not log, store, or process your location beyond this single real-time request.</p>
              </div>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>4. Email Communications</h2>
              <p style={s.body}>Transactional emails (verification links, password resets) are sent via Resend. These emails are not stored by us beyond what is needed to deliver them. Password reset tokens expire after 1 hour. Email verification tokens expire after 1 hour.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>5. Profile Pictures</h2>
              <p style={s.body}>Profile pictures are stored on Cloudinary. When you delete your account, we delete your profile data from our database immediately. Please note that Cloudinary may retain assets in their CDN cache for a short period after deletion — typically no longer than 24 hours.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>6. Backups</h2>
              <p style={s.body}>Our database hosting provider (Railway) may retain automated backups for a limited period as part of their infrastructure. These backups are not accessible by Flakon for any purpose other than disaster recovery and are overwritten on a rolling basis.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>7. How to Delete Your Data</h2>
              <p style={s.body}>You can delete your account and all associated data at any time by going to:</p>
              <div style={s.highlight}>
                <p style={{ ...s.highlightText, fontFamily: 'Cormorant Garamond, serif', fontSize: '1rem', color: '#c9a96e' }}>Profile → Security → Delete Account</p>
              </div>
              <p style={s.body}>This action is immediate and permanent. Your data cannot be recovered after deletion. If you are unable to access your account to delete it, contact us at <a href="mailto:support@flakon.app" style={s.email}>support@flakon.app</a> and we will delete it manually within 30 days as required by GDPR.</p>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>8. Retention Summary</h2>
              <div style={s.highlight}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {[
                      ['Account & profile data', 'Until account deletion'],
                      ['Fragrance library', 'Until account deletion'],
                      ['Journal entries', 'Until account deletion'],
                      ['Location data', 'Never stored'],
                      ['Password reset tokens', '1 hour'],
                      ['Email verification tokens', '1 hour'],
                      ['Profile pictures (Cloudinary)', 'Until account deletion + CDN cache (~24hrs)'],
                      ['Database backups', 'Rolling — managed by Railway'],
                    ].map(([item, period], i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #1a1a1a' }}>
                        <td style={{ padding: '12px 0', fontSize: '0.78rem', color: '#a09890', fontWeight: 300 }}>{item}</td>
                        <td style={{ padding: '12px 0', fontSize: '0.78rem', color: i === 3 ? '#7aad7a' : '#6b6460', fontWeight: 300, textAlign: 'right' }}>{period}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={s.section}>
              <h2 style={s.sectionTitle}>9. Contact</h2>
              <p style={s.body}>For any questions about how we handle your data, contact us at <a href="mailto:support@flakon.app" style={s.email}>support@flakon.app</a>.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Legal;